import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Customer,
  CustomerNote,
  CustomerTask,
  MortgageApplication,
  getAuthHeaders,
  handleApiError,
  formatDate,
  formatPhoneNumber,
  formatCurrency,
  getTaskStatusColor,
  getPriorityColor,
  getCustomerDisplayName,
  makeApiCall,
  makeJsonApiCall
} from '../utils/customerUtils'
import { PlusIcon } from './Icons'
import { PhoneLink, PhoneIcon } from './PhoneLink'
import { CustomerOrgChart } from './CustomerOrgChart'
import { PropertyManagement } from './PropertyManagement'

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }
  return (
    <button
      onClick={handleCopy}
      title={`Copy ${label ?? value}`}
      className="ml-1.5 p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
    >
      {copied
        ? <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        : <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
      }
    </button>
  )
}

interface CustomerProfileProps {
  customerId: number
  onClose: () => void
  onEdit: (customer: Customer) => void
  onCustomerClick?: (customerId: number) => void
  onDealClick?: (dealId: number) => void
}

interface TaskCreationModal {
  isOpen: boolean
}

interface AssociatedCustomer {
  id: number
  name: string
  email: string
  phone: string
  language: string
  association_type: string
  association_created_at: string
  shared_deals: Array<{
    id: number
    name: string
    amount: number
    status_name: string
    status_color: string
  }>
}

export function CustomerProfile({ customerId, onEdit, onClose, onCustomerClick, onDealClick }: CustomerProfileProps) {
  const { t } = useTranslation()
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [associatedCustomers, setAssociatedCustomers] = useState<AssociatedCustomer[]>([])
  const [notes, setNotes] = useState<CustomerNote[]>([])
  const [tasks, setTasks] = useState<CustomerTask[]>([])
  const [customerDeals, setCustomerDeals] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'properties' | 'notes' | 'tasks' | 'deals' | 'associated'>('overview')
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false)

  useEffect(() => {
    if (customerId) {
      fetchCustomerProfile()
    }
  }, [customerId])

  const fetchCustomerProfile = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch customer details, associated customers, notes, tasks, and deals in parallel
      const [customerResponse, associationsResponse, notesResponse, tasksResponse, dealsResponse] = await Promise.all([
        makeApiCall(`api/customers/${customerId}`),
        makeApiCall(`api/customer-associations/${customerId}`),
        makeApiCall(`api/notes?customer_id=${customerId}`),
        makeApiCall(`api/tasks?customer_id=${customerId}`),
        makeApiCall(`api/deals?customer_id=${customerId}`)
      ])

      if (customerResponse.ok) {
        const data = await customerResponse.json()
        setCustomer(data.customer)
      }

      if (associationsResponse.ok) {
        const data = await associationsResponse.json()
        setAssociatedCustomers(data.associations || [])
      }

      if (notesResponse.ok) {
        const data = await notesResponse.json()
        setNotes(data.notes || [])
      }

      if (tasksResponse.ok) {
        const data = await tasksResponse.json()
        setTasks(data.tasks || [])
      }

      if (dealsResponse.ok) {
        const data = await dealsResponse.json()
        setCustomerDeals(data.deals || [])
      }

    } catch (err) {
      console.error('Error fetching customer profile:', err)
      setError(handleApiError(err))
    } finally {
      setLoading(false)
    }
  }

  const getTasksStats = () => {
    const total = tasks.length
    const pending = tasks.filter(t => t.status === 'pending').length
    const inProgress = tasks.filter(t => t.status === 'in_progress').length
    const completed = tasks.filter(t => t.status === 'completed').length
    const overdue = tasks.filter(t => 
      t.status !== 'completed' && new Date(t.due_date) < new Date()
    ).length

    return { total, pending, inProgress, completed, overdue }
  }

  const handleRemoveAssociation = async (associatedCustomer: AssociatedCustomer) => {
    if (confirm(`Remove association with ${associatedCustomer.name}? This will not delete the customer, only remove the association.`)) {
      try {
        const response = await makeApiCall('api/customer-associations', {
          method: 'DELETE',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            customer_id: customerId,
            associated_customer_id: associatedCustomer.id
          })
        })

        if (response.ok) {
          // Refresh the profile to get updated associations list
          await fetchCustomerProfile()
        }
      } catch (err) {
        console.error('Error removing association:', err)
        alert('Failed to remove association.')
      }
    }
  }

  const parseNoteContent = (content: string) => {
    try {
      const parsed = JSON.parse(content)
      if (parsed.summary && parsed.keyPoints) {
        return parsed // AI-generated note
      }
    } catch (e) {
      // Not JSON, treat as regular note
    }
    return { rawContent: content }
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">Loading customer profile...</span>
        </div>
      </div>
    )
  }

  if (error || !customer) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Customer not found'}</p>
          <button 
            onClick={fetchCustomerProfile}
            className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors mr-3"
          >
            Retry
          </button>
          {onClose && (
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    )
  }

  const taskStats = getTasksStats()

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <nav className="flex gap-0 px-4 min-w-max">
          {[
            { id: 'overview', label: t('customers.tabs.overview'), count: null },
            { id: 'deals', label: t('customers.tabs.deals.title'), count: customerDeals.length },
            { id: 'tasks', label: t('customers.tabs.tasks.title'), count: tasks.filter(t => t.status !== 'completed').length },
            { id: 'notes', label: t('customers.tabs.notes.title'), count: notes.length },
            { id: 'properties', label: t('customers.tabs.properties'), count: null },
            { id: 'associated', label: t('customers.tabs.associatedCustomers'), count: associatedCustomers.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-1.5 py-3.5 px-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-gray-900 dark:border-gray-100 text-gray-900 dark:text-gray-100'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-200 dark:hover:border-gray-600'
              }`}
            >
              {tab.label}
              {tab.count !== null && tab.count > 0 && (
                <span className={`py-0.5 px-1.5 rounded-full text-xs font-semibold ${
                  activeTab === tab.id
                    ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Deals', value: customerDeals.length, color: 'blue' },
                { label: 'Open tasks', value: tasks.filter(t => t.status !== 'completed').length, color: 'amber' },
                { label: 'Notes', value: notes.length, color: 'violet' },
                { label: 'Associated', value: associatedCustomers.length, color: 'emerald' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-gray-50 dark:bg-gray-700/60 rounded-xl p-4 border border-gray-100 dark:border-gray-700 text-center">
                  <p className={`text-2xl font-bold text-${color}-600 dark:text-${color}-400`}>{value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Contact info */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">{t('customers.sections.primaryContact')}</h3>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{t('customers.email')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-all">{customer.email}</p>
                    </div>
                    <CopyButton value={customer.email} label="email" />
                  </div>

                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{t('customers.phone')}</p>
                      {customer.phone ? (
                        <PhoneLink phoneNumber={customer.phone} customerId={customer.id} customerName={getCustomerDisplayName(customer)} />
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">Not provided</span>
                      )}
                    </div>
                    {customer.phone && <CopyButton value={customer.phone.replace(/\D/g, '')} label="phone" />}
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{t('customers.labels.preferredLanguage')}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {customer.language === 'en' ? 'English' : 'Français (French)'}
                      <span className="text-xs text-gray-400 dark:text-gray-500">({customer.language?.toUpperCase() || 'FR'})</span>
                    </span>
                  </div>

                  {customer.referred_by && (
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{t('customers.form.referredBy')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.referred_by}</p>
                    </div>
                  )}

                  {customer.custom_link && (
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{t('customers.labels.customLink')}</p>
                      <a href={customer.custom_link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all">
                        {customer.custom_link}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Active deals */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Current Deals</h3>
                  {customerDeals.length > 3 && (
                    <button onClick={() => setActiveTab('deals')} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                      View all {customerDeals.length}
                    </button>
                  )}
                </div>
                <div className="p-5">
                  {customerDeals.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">{t('customers.tabs.deals.noDealsFound')}</p>
                  ) : (
                    <div className="space-y-3">
                      {customerDeals.slice(0, 3).map((deal: any) => (
                        <button
                          key={deal.id}
                          onClick={() => onDealClick && onDealClick(deal.id)}
                          className="w-full text-left rounded-lg border border-gray-100 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-sm p-3 transition-all group"
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate pr-2">
                              {deal.name || deal.title || `${formatCurrency(deal.amount)} Deal`}
                            </span>
                            <span
                              className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
                              style={{ borderColor: `${deal.status_color}50`, backgroundColor: `${deal.status_color}15`, color: deal.status_color }}
                            >
                              {deal.status_name || deal.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                            {deal.amount && <span>{formatCurrency(deal.amount)}</span>}
                            {deal.type_name && <span>· {deal.type_name}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Open tasks */}
            {tasks.filter(t => t.status !== 'completed').length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Open Tasks</h3>
                  {tasks.filter(t => t.status !== 'completed').length > 4 && (
                    <button onClick={() => setActiveTab('tasks')} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                      View all
                    </button>
                  )}
                </div>
                <div className="divide-y divide-gray-50 dark:divide-gray-700">
                  {tasks.filter(t => t.status !== 'completed').slice(0, 4).map((task) => (
                    <div key={task.id} className="px-5 py-3 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${task.status === 'in_progress' ? 'bg-blue-400' : 'bg-amber-400'}`} />
                        <span className="text-sm text-gray-800 dark:text-gray-200 truncate">{task.description}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {task.priority && (
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium border ${getPriorityColor(task.priority)}`}>{task.priority}</span>
                        )}
                        <span className="text-xs text-gray-400 dark:text-gray-500">{formatDate(task.due_date)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'properties' && customer && (
          <PropertyManagement 
            customerId={customer.id}
            customerName={getCustomerDisplayName(customer)}
            onCreateTask={async (propertyId, taskType, description, dueDate) => {
              // Refresh tasks after creating a new task
              await fetchCustomerProfile()
            }}
          />
        )}

        {activeTab === 'associated' && customer && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('customers.relationships.title')}</h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">{associatedCustomers.length} {t('customers.relationships.associations')}</span>
            </div>

            <CustomerOrgChart 
              customerId={customer.id} 
              customerName={getCustomerDisplayName(customer)}
              onCustomerClick={onCustomerClick}
              onDealClick={onDealClick}
            />
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">Association Management</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                Customer associations are created automatically when customers are added to deals together or when multiple customers are created at the same time.
              </p>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>• <strong>Created Together:</strong> Customers added as "Associated Customers" during creation</li>
                <li>• <strong>Deal Together:</strong> Customers who share one or more deals as applicant/co-applicants</li>
                <li>• Removing an association doesn't delete the customer, only the relationship</li>
                <li>• Shared deals are automatically displayed when customers have deals together</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('customers.tabs.notes.title')}</h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">{notes.length} {t('customers.tabs.notes.totalNotes')}</span>
            </div>

            {notes.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('customers.tabs.notes.noNotesFound')}
              </div>
            ) : (
              <div className="space-y-4">
                {notes.map((note) => {
                  const parsedContent = parseNoteContent(note.content)
                  const isAiNote = 'summary' in parsedContent

                  return (
                    <div key={note.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          {isAiNote && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              AI Summary
                            </span>
                          )}
                          <span className="text-sm font-medium text-gray-900">
                            {note.created_by_username || 'System'}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500">{formatDate(note.created_at)}</span>
                      </div>

                      {isAiNote ? (
                        <div className="space-y-3">
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Summary:</h4>
                            <p className="text-gray-700">{parsedContent.summary}</p>
                          </div>
                          {parsedContent.keyPoints && parsedContent.keyPoints.length > 0 && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Key Points:</h4>
                              <ul className="list-disc list-inside text-gray-700 space-y-1">
                                {parsedContent.keyPoints.map((point: string, index: number) => (
                                  <li key={index}>{point}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {parsedContent.actionItems && parsedContent.actionItems.length > 0 && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Action Items:</h4>
                              <ul className="list-disc list-inside text-gray-700 space-y-1">
                                {parsedContent.actionItems.map((item: string, index: number) => (
                                  <li key={index}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-700">{parsedContent.rawContent || note.content}</p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('customers.tabs.tasks.title')}</h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">{tasks.length} {t('customers.tabs.tasks.totalTasks')}</span>
            </div>

            {tasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('customers.tabs.tasks.noTasksFound')}
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.map((task) => (
                  <div key={task.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{task.description}</h4>
                      <div className="flex items-center space-x-2">
                        {task.priority && (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        )}
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getTaskStatusColor(task.status)}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <span>{t('customers.tabs.tasks.due')}: {formatDate(task.due_date)}</span>
                      <span>{t('customers.tabs.tasks.created')}: {formatDate(task.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'deals' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('customers.tabs.deals.title')}</h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">{customerDeals.length} {t('customers.tabs.deals.dealsCount')}</span>
            </div>

            {customerDeals.length === 0 ? (
              <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                <svg className="w-10 h-10 mx-auto mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('customers.tabs.deals.noDealsFound')}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {customerDeals.map((deal: any) => (
                  <button
                    key={deal.id}
                    onClick={() => onDealClick && onDealClick(deal.id)}
                    className="w-full text-left rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md p-4 transition-all group"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                          {deal.name || deal.title || `${formatCurrency(deal.amount)} Deal`}
                        </h4>
                        {deal.type_name && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{deal.type_name}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border"
                          style={{ borderColor: `${deal.status_color}60`, backgroundColor: `${deal.status_color}18`, color: deal.status_color }}
                        >
                          {deal.status_name || deal.status}
                        </span>
                        <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-blue-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                      {deal.amount && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          {formatCurrency(deal.amount)}
                        </span>
                      )}
                      {deal.lender_name && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                          {deal.lender_name}
                        </span>
                      )}
                      {deal.closing_date && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          Closing {formatDate(deal.closing_date)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        Created {formatDate(deal.created_at)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Task Modal - CUSTOMER FOCUSED */}
      {showCreateTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Quick Task for {getCustomerDisplayName(customer)}
              </h3>
              <button
                onClick={() => setShowCreateTaskModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault()
              const formData = new FormData(e.currentTarget)
              const description = formData.get('description') as string
              const priority = formData.get('priority') as string || 'medium'
              const selectedDate = formData.get('selected_date') as string
              const customDate = formData.get('custom_date') as string
              
              try {
                const token = localStorage.getItem('token')
                if (!token) throw new Error('Authentication required')

                // Use selected date or custom date, default to tomorrow
                let taskDueDate = selectedDate || customDate
                if (!taskDueDate) {
                  const tomorrow = new Date()
                  tomorrow.setDate(tomorrow.getDate() + 1)
                  taskDueDate = tomorrow.toISOString().split('T')[0]
                }

                const response = await makeApiCall('api/tasks', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({
                    customer_id: customerId,
                    description,
                    priority,
                    due_date: taskDueDate,
                    status: 'pending'
                  })
                })

                if (!response.ok) throw new Error('Failed to create task')

                await fetchCustomerProfile()
                setShowCreateTaskModal(false)
              } catch (err) {
                console.error('Error creating task:', err)
                alert('Failed to create task. Please try again.')
              }
            }}>
              <div className="space-y-6">
                {/* Task Description - AUTO FOCUS */}
                <div>
                  <textarea
                    name="description"
                    placeholder="What needs to be done?"
                    className="w-full px-4 py-3 text-lg border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:border-green-500 focus:outline-none transition-colors"
                    rows={2}
                    required
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.metaKey) {
                        e.preventDefault()
                        e.currentTarget.form?.requestSubmit()
                      }
                    }}
                  />
                </div>

                {/* Priority Selection */}
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Priority Level
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <label className="flex">
                      <input
                        type="radio"
                        name="priority"
                        value="low"
                        className="sr-only peer"
                      />
                      <div className="w-full p-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-center cursor-pointer transition-all peer-checked:border-green-500 peer-checked:bg-green-50 dark:peer-checked:bg-green-900/20 hover:border-gray-300">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400 peer-checked:text-green-700 dark:peer-checked:text-green-300">
                          Low
                        </div>
                      </div>
                    </label>
                    <label className="flex">
                      <input
                        type="radio"
                        name="priority"
                        value="medium"
                        defaultChecked
                        className="sr-only peer"
                      />
                      <div className="w-full p-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-center cursor-pointer transition-all peer-checked:border-yellow-500 peer-checked:bg-yellow-50 dark:peer-checked:bg-yellow-900/20 hover:border-gray-300">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400 peer-checked:text-yellow-700 dark:peer-checked:text-yellow-300">
                          Medium
                        </div>
                      </div>
                    </label>
                    <label className="flex">
                      <input
                        type="radio"
                        name="priority"
                        value="high"
                        className="sr-only peer"
                      />
                      <div className="w-full p-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-center cursor-pointer transition-all peer-checked:border-red-500 peer-checked:bg-red-50 dark:peer-checked:bg-red-900/20 hover:border-gray-300">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400 peer-checked:text-red-700 dark:peer-checked:text-red-300">
                          High
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Quick Date Selection */}
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    When is this due?
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {Array.from({ length: 7 }, (_, i) => {
                      const date = new Date()
                      date.setDate(date.getDate() + i)
                      
                      let label = ''
                      if (i === 0) label = 'Today'
                      else if (i === 1) label = 'Tomorrow'
                      else label = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                      
                      return (
                        <label key={i} className="flex">
                          <input
                            type="radio"
                            name="selected_date"
                            value={date.toISOString().split('T')[0]}
                            className="sr-only peer"
                          />
                          <div className="w-full p-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-center cursor-pointer transition-all peer-checked:border-green-500 peer-checked:bg-green-50 dark:peer-checked:bg-green-900/20 hover:border-gray-300">
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400 peer-checked:text-green-700 dark:peer-checked:text-green-300">
                              {label}
                            </div>
                          </div>
                        </label>
                      )
                    })}
                  </div>

                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Or:</span>
                    <input
                      type="date"
                      name="custom_date"
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  ⌘ + Enter to create
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateTaskModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Create Task
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
} 